import { Grid, makeStyles, Theme } from '@material-ui/core';
import { Body, Icon, Tooltip } from 'copilot-design-system';
import { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 } from 'uuid';

import { SignatureDateIcon, SignatureTextIcon } from 'src/components/icons';
import {
  CONTRACT_COMPONENT_DEFAULT_HEIGHT,
  CONTRACT_COMPONENT_DEFAULT_WIDTH,
  SignatureComponentType,
} from 'src/constants';
import { ContractBuilderContext } from 'src/context/contractBuilderContext';
import { useAppSelector } from 'src/hooks/useStore';
import {
  SignaturePopoverForm,
  SignatureSidebarButton,
} from 'src/legacy/components/Signature/SignatureComponents';
import { useClientInputLabelForm } from 'src/legacy/components/Signature/SignatureSidebar/useClientInputLabelForm';
import MemoBaseTypography from 'src/legacy/components/Text/BaseTypography';
import { InputType } from 'src/services/api';
import {
  AddComponentAction,
  AddPlaceholderComponentAction,
  RemovePlaceholderComponentAction,
  SetSelectedComponent,
} from 'src/store/signaturePage/actions';
import {
  ComponentCoordinatesType,
  SignaturePageComponent,
} from 'src/store/signaturePage/types';
import { alertSnackbar } from 'src/store/ui/actions';
import {
  getFieldTypeUsingComponentType,
  hasSignatureCompnentsWithDuplicateLabels,
} from 'src/utils/ContractUtils';

const VARIABLE_INPUT_TOOLTIP_TEXT = `Each time you share a contract with a client, you'll be prompted to provide these values.`;

const useStyles = makeStyles((theme: Theme) => ({
  inputTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
  },
}));

/**
 * This component renders the contract sidebar variable inputs
 */
export const VariableInputs = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [droppedComponent, setDroppedComponent] =
    useState<SignaturePageComponent | null>(null);
  const { placeholderElement } = useContext(ContractBuilderContext);
  const [labelModalInfo, setLabelModalInfo] = useState<{
    isOpen: boolean;
    componentType: SignatureComponentType | null;
  }>({
    isOpen: false,
    componentType: null,
  });
  const pageComponents = useAppSelector(
    (state) => state.signaturePage.pageComponents,
  );

  const handleComponentDropped = (
    coords: ComponentCoordinatesType,
    componentType: SignatureComponentType,
  ) => {
    const key = v4();
    const stagedComponent = {
      componentType,
      key,
      fields: { key, value: '' },
      ...coords,
      height: CONTRACT_COMPONENT_DEFAULT_HEIGHT,
      width: CONTRACT_COMPONENT_DEFAULT_WIDTH,
    };

    setDroppedComponent(stagedComponent);

    dispatch(AddPlaceholderComponentAction(stagedComponent));
    setLabelModalInfo({
      isOpen: true,
      componentType,
    });
  };

  const handleCloseModal = () => {
    setLabelModalInfo({
      isOpen: false,
      componentType: null,
    });
    dispatch(RemovePlaceholderComponentAction());
  };

  const handleSaveComponent = (formValues: { label: string }) => {
    if (!droppedComponent) return;

    const hasDuplicateLabels = hasSignatureCompnentsWithDuplicateLabels(
      pageComponents,
      formValues.label,
    );

    if (hasDuplicateLabels) {
      dispatch(
        alertSnackbar({
          errorMessage: 'This label is already in use. Please try again.',
        }),
      );
      return;
    }

    const stagedComponent = {
      ...droppedComponent,
      fields: {
        key: droppedComponent.key,
        value: '',
      },
      label: formValues.label,
      name: formValues.label,
      inputType: InputType.Variable,
      fieldType: getFieldTypeUsingComponentType(droppedComponent.componentType),
    };
    dispatch(AddComponentAction(stagedComponent));
    dispatch(SetSelectedComponent(stagedComponent.key));

    handleCloseModal();
  };

  return (
    <>
      <div className={classes.inputTitle}>
        <MemoBaseTypography fontType="12Medium">
          Variable inputs
        </MemoBaseTypography>
        <Tooltip
          position="bottom"
          triggerClassname="inline-block"
          content={
            <Body size="sm" className="max-w-[288px]">
              {VARIABLE_INPUT_TOOLTIP_TEXT}
            </Body>
          }
        >
          <Icon icon="Info" className="h-3 w-4 text-gray-small" />
        </Tooltip>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SignatureSidebarButton
            label="Date"
            onDrop={(coordinates) =>
              handleComponentDropped(
                coordinates,
                SignatureComponentType.REQUEST_DATE,
              )
            }
            buttonIcon={SignatureDateIcon}
            isClientSignatureButton
          />
        </Grid>
        <Grid item xs={12}>
          <SignatureSidebarButton
            label="Text"
            onDrop={(coords) =>
              handleComponentDropped(
                coords,
                SignatureComponentType.REQUEST_TEXT,
              )
            }
            buttonIcon={SignatureTextIcon}
            isClientSignatureButton
          />
        </Grid>
      </Grid>
      <SignaturePopoverForm
        key="variable-inputs"
        initialFormValue={{
          label: '',
        }}
        title={`Variable ${
          labelModalInfo.componentType === SignatureComponentType.REQUEST_DATE
            ? 'date'
            : 'text'
        } field`}
        actionLabel="Add"
        handleSave={handleSaveComponent}
        onClose={handleCloseModal}
        useFormHook={() =>
          useClientInputLabelForm({
            componentType: labelModalInfo.componentType,
            isRequired: true,
          })
        }
        open={labelModalInfo.isOpen}
        anchorEl={placeholderElement}
      />
    </>
  );
};
